'use client';

import { useState } from 'react';
import type { ColumnConfig } from '../types';

interface GroupingAreaProps {
  columns: ColumnConfig[];
  groupedColumns: string[];
  onGroupChange: (groupedColumns: string[]) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
}

export const GroupingArea: React.FC<GroupingAreaProps> = ({
  columns,
  groupedColumns,
  onGroupChange,
  onDragOver,
  onDrop,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
    onDragOver(e);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    onDrop(e);
  };

  const removeGroup = (columnField: string) => {
    const updatedGroups = groupedColumns.filter(field => field !== columnField);
    onGroupChange(updatedGroups);
  };

  const clearAllGroups = () => {
    onGroupChange([]);
  };

  const getColumnName = (field: string): string => {
    // First check top-level columns
    const column = columns.find(col => col.field === field);
    if (column?.columnName) return column.columnName;

    // Search in subColumns for nested column structures
    for (const col of columns) {
      if (col.subColumns) {
        const subCol = col.subColumns.find((sub: any) => sub.field === field);
        if (subCol?.columnName) return subCol.columnName;
      }
    }

    return field;
  };

  return (
    <div className="mb-4">
      {groupedColumns.length > 0 && (
        <div className="flex items-center justify-end mb-2">
          <button
            onClick={clearAllGroups}
            className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
          >
            Clear All Groups
          </button>
        </div>
      )}

      <div
        className={`min-h-16 border-2 border-dashed rounded-lg p-3 transition-colors ${
          isDragOver
            ? 'border-violet-400 bg-violet-50 dark:bg-violet-900/30'
            : groupedColumns.length > 0
            ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {groupedColumns.length === 0 ? (
          <div className="flex items-center justify-center h-10">
            <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
              <i className="ri-drag-drop-line mr-2 w-4 h-4 flex items-center justify-center"></i>
              Drag columns here to group data
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {groupedColumns.map((field, index) => (
              <div
                key={field}
                className="flex items-center bg-violet-100 dark:bg-violet-900/50 text-violet-800 dark:text-violet-200 px-3 py-1 rounded-full text-sm"
              >
                {index > 0 && (
                  <i className="ri-arrow-right-line mr-1 w-3 h-3 flex items-center justify-center"></i>
                )}
                <span>{getColumnName(field)}</span>
                <button
                  onClick={() => removeGroup(field)}
                  className="ml-2 text-violet-600 dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300"
                >
                  <i className="ri-close-line w-3 h-3 flex items-center justify-center"></i>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {groupedColumns.length > 0 && (
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
          <i className="ri-information-line mr-1 w-3 h-3 flex items-center justify-center"></i>
          Data is grouped by {groupedColumns.length} column{groupedColumns.length > 1 ? 's' : ''}.
          Click expand/collapse icons to view sub-records.
        </div>
      )}
    </div>
  );
};